import {absoluteUrl, brandLogoFor, ogImageFor, SITE_NAME, type SiteLocale} from "./siteMeta";
import {bakedPricingForContext} from "pricing/bakedPricing";
import {PLAN_CATALOGUE, PlanId} from "pricing/planCatalogue";

/**
 * schema.org JSON-LD builders for the public pages (ui#97). Pure functions returning plain objects
 * so they are trivially unit-testable and can be serialised by {@link ../seo/Seo.tsx} through the
 * SSG head bridge into a `<script type="application/ld+json">`.
 *
 * Locale-aware (ui#101): every `url`/`logo` is built from the caller's `origin` (transferify.cloud
 * for English, transferify.ro for Romanian — see {@link ./siteMeta.siteOrigin}) so the JSON-LD `url`s
 * always match the page's canonical and never point cross-domain. Callers resolve that origin from the
 * page's CANONICAL locale — the serving tree/host's, not the reader's (#1135) — for the same reason the
 * canonical does. The `featureList` is passed in from the active catalog so the advertised
 * differentiators read in the page's language: copy follows the reader, identity follows the URL.
 *
 * Kept minimal and honest: no `AggregateRating` (we have no real reviews) and prices sourced from the
 * same {@link ../pricing/pricingPlans.ts} catalogue the pricing page paints, so the markup can never
 * drift from the visible amounts.
 */

const SCHEMA_CONTEXT = "https://schema.org";

/**
 * The stable node id of the brand's {@link organizationLd}, per origin.
 *
 * A fragment on the site's own URL is the schema.org convention for "the organization this site
 * belongs to", as distinct from the home page document that describes it. Having one lets other
 * entities REFERENCE the organization — `author`, `publisher` — instead of restating its name, url and
 * logo, so a crawler that meets the brand on an article and on the home page sees one node rather than
 * two lookalikes.
 *
 * Per-origin, like every other identity value here (#1135): transferify.ro's organization node is
 * `https://transferify.ro/#organization`, not the .cloud one. The two brands are separately indexed
 * sites, and a shared id would tell Google the Romanian corpus is published by the English site.
 */
export function organizationId(origin: string): string {
	return `${origin}/#organization`;
}

/**
 * The brand as another entity's `author` or `publisher`.
 *
 * Deliberately SELF-DESCRIBING rather than a bare `{"@id": …}` pointer. Google's author guidance names
 * `@type` and `url` as what it uses to work out who an author is and documents `@id` nowhere in that
 * context; node merging by `@id` does happen, but it is an undocumented behaviour to hang 28 pages'
 * attribution on. Carrying the type, the name and the url inline means the author reads correctly from
 * this entity alone, while the `@id` still merges it into the {@link organizationLd} node beside it
 * rather than standing up a second, lookalike organization.
 */
function organizationRef(origin: string): object {
	return {"@type": "Organization", "@id": organizationId(origin), name: SITE_NAME, url: origin};
}

/**
 * Organization entity — brand identity for the knowledge panel, and the node every article names as
 * its `author` and `publisher`.
 *
 * Carries an `@id` (#1544) so those references resolve to it. Without one, `author` on 28 corpus pages
 * would have to repeat the whole entity or point at nothing.
 */
export function organizationLd(origin: string): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "Organization",
		"@id": organizationId(origin),
		name: SITE_NAME,
		url: origin,
		logo: brandLogoFor(origin),
	};
}

/** WebSite entity — the canonical site node. */
export function webSiteLd(origin: string): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "WebSite",
		name: SITE_NAME,
		url: origin,
	};
}

/**
 * SoftwareApplication entity for the home page — a free web utility. `featureList` advertises the
 * differentiators (E2EE / P2P / no account) that competitors' markup also carries; it comes from the
 * active locale catalog so it reads in the page's language.
 *
 * `currencyCode` was a hardcoded `"EUR"` on every host until #1136 — the last currency in the SEO layer
 * chosen from neither locale nor host. It misquoted nobody, because the price is `"0"` and zero is zero
 * in any denomination, but a free offer denominated in a currency the page never mentions is a claim
 * about the storefront, and transferify.ro's is not the euro one. Callers pass
 * {@link canonicalCurrencyCode}, which resolves it exactly as they resolve `origin` — from the document's
 * canonical locale, not the reader's.
 */
export function softwareApplicationLd(origin: string, featureList: string[], currencyCode: string): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "SoftwareApplication",
		name: SITE_NAME,
		url: origin,
		applicationCategory: "UtilitiesApplication",
		operatingSystem: "Web",
		offers: {"@type": "Offer", price: "0", priceCurrency: currencyCode},
		featureList,
	};
}

/** The home page's structured data: Organization + WebSite + SoftwareApplication. */
export function homeJsonLd(origin: string, featureList: string[], currencyCode: string): object[] {
	return [organizationLd(origin), webSiteLd(origin), softwareApplicationLd(origin, featureList, currencyCode)];
}

/**
 * The ISO-4217 code an entity that carries NO amount denominates its offer in (#1136) — currently the
 * home page's free `SoftwareApplication`.
 *
 * Takes the document's CANONICAL locale, the same input its `origin` takes (#1135), because a currency
 * on a price of `"0"` is identity rather than money: it says which storefront this URL is, and a value
 * that moved with the reader's persisted language would make one URL claim two storefronts to two
 * visitors — the same defect #1135 removed from the canonical itself. Copy follows the reader, identity
 * follows the URL, and this is identity.
 *
 * Deliberately NOT what {@link pricingOffersForContext} uses. That entity quotes REAL amounts a reader
 * can be charged, so its currency has to be the one their checkout would take (locale AND host); this
 * one denominates nothing, so it can and must follow the URL instead. The two answers differ only for a
 * reader carrying a `localStorage` language override, which no crawler does.
 *
 * Resolved through the same baked snapshot the cards read, with no host argument, so a storefront whose
 * currency the bake does not carry falls back exactly as every other pricing surface does rather than
 * naming a currency this build has no amounts for.
 */
export function canonicalCurrencyCode(canonicalLocale: string): string {
	return bakedPricingForContext(canonicalLocale, NO_HOST).currency.toUpperCase();
}

/**
 * The "no host signal" input the pricing rules take — see `utils/useMountedHostname`.
 *
 * Two reasons, neither of them "we could not get the host". It is REDUNDANT: the host's say is already
 * folded into {@link canonicalCurrencyCode}'s argument (`i18n/localePreference.canonicalLocale` is the
 * tree's forced locale or the host's, never the reader's), so passing the hostname would return the
 * identical answer for every reachable pair. And it is UNAVAILABLE: this runs during SSG pre-render and
 * on the first client render, where reading `window` needs the `useMountedHostname` gate that a plain
 * function cannot have.
 */
const NO_HOST = "";

/** One question/answer pair for {@link faqPageLd}. Shape matches {@link ../home/landing/faq.ts}'s
 * `FaqItem` so the home FAQ data feeds the builder directly — single source, no re-mapping. */
export interface FaqItemLd {
	question: string;
	answer: string;
}

/**
 * FAQPage structured data. Each item becomes a schema.org `Question` with an `acceptedAnswer`. Fed the
 * SAME array that renders the visible FAQ band, so the markup and the on-page questions can never
 * diverge.
 *
 * **It earns no search feature and is not what any page relies on (#1544).** Google removed FAQ rich
 * results from Search in May 2026, dropped the Search Console FAQ report and Rich Results Test support
 * in June 2026, and stopped accepting FAQPage through the API in August 2026. The comment here used to
 * claim the markup still fed Google's PAA and AI overviews; there is no evidence for that and it read
 * as a reason to keep investing in the entity, so it is gone.
 *
 * The markup stays because it costs nothing and is still a machine-readable question/answer pairing
 * for the non-Google readers of these pages. What describes a content page is now its
 * {@link contentArticleLd}, not this.
 */
export function faqPageLd(items: readonly FaqItemLd[]): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {"@type": "Answer", text: item.answer},
		})),
	};
}

/** One crumb of a {@link breadcrumbLd} trail: the label a reader sees and the page it points at. */
export interface BreadcrumbLd {
	/** Visible label, in the page's own language (the Romanian trail reads "Acasă", not "Home"). */
	name: string;
	/** The crumb's locale-less path (`"/"` for the tree's home), resolved against the caller's origin. */
	path: string;
}

/**
 * BreadcrumbList structured data — the trail Google paints in place of the raw URL in a result.
 *
 * `position` is 1-based and derived from the array order, so a trail cannot be numbered wrong; every
 * `item` is resolved through the same {@link ../seo/siteMeta.absoluteUrl} the canonical uses, on the
 * caller's own locale origin. That sameness is the point: a crumb URL that disagreed with the page's
 * canonical — a different origin, or a trailing slash on the home crumb — would advertise a second URL
 * for content the canonical already claims, which is how duplicate-content reports start.
 */
export function breadcrumbLd(origin: string, trail: readonly BreadcrumbLd[]): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "BreadcrumbList",
		itemListElement: trail.map((crumb, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: crumb.name,
			item: absoluteUrl(origin, crumb.path),
		})),
	};
}

/** The facts one content page states about itself as an {@link contentArticleLd} (#1544). */
export interface ContentArticleLdInput {
	/** The page's own origin, so the article, its author and its breadcrumbs name ONE site. */
	origin: string;
	/** The page's path in its canonical locale — the URL this article IS. */
	path: string;
	/** The page's `<h1>` and `<title>`, which is also the article's headline. */
	title: string;
	/** The page's meta description, reused verbatim so the two can never describe it differently. */
	description: string;
	/** BCP-47 language of the body copy — the file's own directory locale, not the reader's. */
	locale: SiteLocale;
	/** `YYYY-MM-DD` of the last commit that touched the markdown (`ContentPageEntry.lastmod`). */
	lastmod: string;
}

/**
 * The `Article` entity one markdown content page emits (#1544), authored and published by the brand's
 * {@link organizationLd}.
 *
 * **Why the organization is the author, and not a person.** The corpus carries no byline, no author
 * page and no editorial-policy page — nothing in the HTML or on screen names a writer. A `Person`
 * author would therefore be a claim the site does not back anywhere a reader can check, which is
 * exactly the kind of markup Google's structured-data policy treats as misleading. The organization IS
 * the truthful answer: these pages are written and published by Transferify, and the Organization node
 * they point at is the same one the home page has always declared, at a resolvable URL with a logo.
 * Giving them a named human author is a CONTENT decision — it needs a real writer and a visible byline
 * first — and is filed separately rather than invented here.
 *
 * `datePublished` is deliberately absent. Google lists it as recommended, not required, and says the
 * Rich Results Test raises no warning for it; this repo has one honest date per page, the commit that
 * last touched the file. A first-commit date would call a file rename a publication, so an omitted
 * recommended field beats a wrong stated one.
 *
 * `image` is the page's own `og:image`, resolved through the same {@link ../seo/siteMeta.ogImageFor}
 * the `<meta>` uses, so the entity cannot advertise a cover the page does not.
 */
export function contentArticleLd({
	origin,
	path,
	title,
	description,
	locale,
	lastmod,
}: ContentArticleLdInput): object {
	const url = absoluteUrl(origin, path);
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "Article",
		"@id": `${url}#article`,
		headline: title,
		description,
		inLanguage: locale,
		url,
		mainEntityOfPage: {"@type": "WebPage", "@id": url},
		image: ogImageFor(origin),
		dateModified: lastmod,
		author: organizationRef(origin),
		publisher: organizationRef(origin),
	};
}

/** What a markdown content page knows about itself when {@link contentArticleJsonLd} builds its markup. */
export interface ContentArticleJsonLdInput {
	/**
	 * The page's own origin — the one `<Seo>` canonicalizes on, from `siteOrigin(canonicalLocale)`
	 * (#1135), so the trail and the canonical cannot name two sites.
	 */
	origin: string;
	/**
	 * The page's path in its CANONICAL locale, or `undefined` when that locale ships no path for it —
	 * exactly when the canonical is suppressed too.
	 */
	path: string | undefined;
	/** The page title, which is also the trail's leaf crumb and the article's headline. */
	title: string;
	/** The page's meta description, which is also the article's. */
	description: string;
	/** The document's own locale — the language the body copy is written in. */
	locale: SiteLocale;
	/** `YYYY-MM-DD` of the last commit that touched this page's markdown (#1542's git lookup). */
	lastmod: string;
	/** Localized label for the tree's home crumb (`content.breadcrumbHome`). */
	homeLabel: string;
	/** The page's `faq` frontmatter entries; empty for a page that declares none. */
	faq: readonly FaqItemLd[];
}

/**
 * The structured data one markdown content page emits (ui#1005, extended by #1544): a
 * {@link contentArticleLd} describing the page itself, the {@link organizationLd} its author and
 * publisher point at, a two-level {@link breadcrumbLd} trail from its language tree's home to itself,
 * and its `faq` frontmatter as a {@link faqPageLd}. The corpus is flat — every page lives directly
 * under the tree root — so two levels IS the full trail; a deeper one would have to invent a section
 * that no URL contains.
 *
 * **The organization node is emitted on the page, not only linked to.** A `{"@id": …}` reference
 * resolves against the data a crawler has in hand for THIS document; pointing at a node that only the
 * home page declares would leave 28 articles with an author that names nobody — which is the state this
 * markup exists to end.
 *
 * Every entity is conditional on `path`, and that condition mirrors a decision already made elsewhere
 * rather than inventing a second one: no path in the canonical locale means no URL, which is exactly
 * when {@link ../seo/siteMeta.indexingUrlsFor} emits no canonical either. An article, a trail and an
 * `@id` all have to name a URL, and building them anyway would mint
 * `https://transferify.roundefined`. The FAQ block additionally needs `faq` frontmatter — an empty
 * `FAQPage` describes nothing.
 *
 * Returns `undefined` rather than `[]` for a page with no URL: `<Seo>` gates its script on the prop's
 * truthiness, and an empty array is truthy — it would emit a `<script type="application/ld+json">[]`.
 *
 * Lives here, beside the builders, rather than in the page component: which entities a page emits is a
 * rule, and rules stay out of React (the repo's `logic-outside-react` mandate) so they are testable
 * against literals and survive a rewrite of the view.
 */
export function contentArticleJsonLd({
	origin,
	path,
	title,
	description,
	locale,
	lastmod,
	homeLabel,
	faq,
}: ContentArticleJsonLdInput): object[] | undefined {
	if (path === undefined) {
		return undefined;
	}
	const entities: object[] = [
		contentArticleLd({origin, path, title, description, locale, lastmod}),
		organizationLd(origin),
		breadcrumbLd(origin, [{name: homeLabel, path: "/"}, {name: title, path}]),
	];
	if (faq.length > 0) {
		entities.push(faqPageLd(faq));
	}
	return entities;
}

/** One pricing tier for {@link pricingJsonLd}: a display name and its whole-euro monthly price. */
export interface PricingTierLd {
	name: string;
	/** Monthly price as a decimal string, e.g. "12". */
	price: string;
}

export interface PricingOfferData {
	tiers: PricingTierLd[];
	currencyCode: string;
}

/**
 * Build all four offers in canonical catalogue order from this page's baked monthly prices.
 *
 * The context — not the locale alone — resolves the currency here for the same reason it does on the
 * cards: structured data is a price QUOTE, and a crawler that read EUR off a page whose checkout
 * charges RON would be indexing an amount nobody can be charged. At SSG time `hostname` is `""`, so
 * each pre-rendered tree emits its own locale's currency (the `/ro` tree RON, the root tree EUR) —
 * which is exactly right, because that is the tree each host serves. After mount the host wins, so an
 * English-locale reader on transferify.ro emits RON offers under English tier names: the currency the
 * page displays and charges, in the language the page is being read in.
 *
 * SCOPE, stated because the sibling `url` invites the opposite reading: this resolves the OFFER's money
 * and nothing else. The entity's `url` is the caller's, and since #1135 every caller derives it from the
 * page's CANONICAL locale — the host's ({@link ../pages/PricingRoute}, {@link ./Seo} for the matching
 * canonical). The two therefore agree for every crawler and every reader without a persisted language
 * override, and in the one state that has one — `ro` chosen on transferify.cloud — they deliberately do
 * not: the entity keeps the `.cloud` url and quotes RON, because RON is what that reader's checkout
 * charges. The money is right and the attribution is right; what cannot be had is both plus one
 * storefront, without changing what that page charges (#1136). Contrast {@link canonicalCurrencyCode},
 * which CAN follow the url precisely because it denominates nothing.
 */
export function pricingOffersForContext(
	locale: string,
	hostname: string,
	planNames: Record<PlanId, string>,
): PricingOfferData {
	const pricing = bakedPricingForContext(locale, hostname);
	return {
		currencyCode: pricing.currency.toUpperCase(),
		tiers: PLAN_CATALOGUE.map(({id}) => ({
			name: planNames[id],
			price: id === "free" ? "0" : String(pricing[id].monthly),
		})),
	};
}

/**
 * The pricing page's structured data: the SoftwareApplication with an `offers` array, one
 * {@link https://schema.org/Offer Offer} per tier. `currencyCode` is the ISO-4217 code for the
 * domain (EUR on transferify.cloud, RON on transferify.ro — ui#116). Prices are the caller's
 * build-baked amounts from the repo's pricing catalogue (this markup is emitted at SSG time, with no
 * runtime `/plan` fetch); it mirrors the same baked amounts the cards render and is currency-correct
 * per domain.
 */
export function pricingJsonLd(origin: string, tiers: PricingTierLd[], currencyCode = "EUR"): object {
	return {
		"@context": SCHEMA_CONTEXT,
		"@type": "SoftwareApplication",
		name: SITE_NAME,
		url: `${origin}/pricing`,
		applicationCategory: "UtilitiesApplication",
		operatingSystem: "Web",
		offers: tiers.map((tier) => ({
			"@type": "Offer",
			name: tier.name,
			price: tier.price,
			priceCurrency: currencyCode,
		})),
	};
}
